import { useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import AppLayout from "@/layout/AppLayout";
import Avatar from "@/components/app/Avatar";
import { formatMonthYear } from "@/lib/timeHelpers";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, MapPin, ExternalLink, Settings } from "lucide-react";
import MyLibrarySection from "@/components/app/MyLibrarySection";
import { EventBadge } from "@/components/app/EventBadge";

export default function ProfileShow() {
  const { profile, upcoming, past, is_own_profile, library_games, routes } = usePage().props;
  const [tab, setTab] = useState("upcoming");

  const events = tab === "upcoming" ? upcoming : past;

  return (
    <AppLayout>
      <div className="container py-8 max-w-7xl mx-auto">
        {/* Profile header */}
        <div className="flex items-start gap-5 mb-8">
          <Avatar user={profile} size="lg" ring />

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold leading-tight">{profile.full_name}</h1>
                {profile.username && (
                  <p className="text-muted-foreground text-sm">@{profile.username}</p>
                )}
                {(profile.city || profile.state) && (
                  <p className="text-muted-foreground text-sm flex items-center gap-1 mt-0.5">
                    <MapPin className="h-3.5 w-3.5" />
                    {[profile.city, profile.state].filter(Boolean).join(", ")}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Member since {formatMonthYear(new Date(profile.member_since))}
                </p>
              </div>

              {is_own_profile && (
                <Button asChild variant="outline" size="sm">
                  <Link href={routes.settings}>
                    <Settings className="h-4 w-4" />
                    Edit profile
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Event history */}
        <div className="mb-6">
          <div className="flex gap-1 border-b border-border mb-4">
            <TabButton active={tab === "upcoming"} onClick={() => setTab("upcoming")}>
              Upcoming
              {upcoming.length > 0 && (
                <span className="ml-1.5 rounded-full bg-primary/10 text-primary text-[11px] font-medium px-1.5 py-0.5">
                  {upcoming.length}
                </span>
              )}
            </TabButton>
            <TabButton active={tab === "past"} onClick={() => setTab("past")}>
              Past
            </TabButton>
          </div>

          {events.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">
                {tab === "upcoming"
                  ? "No upcoming events"
                  : "No past events yet"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {events.map((participation, i) => (
                <EventCard key={i} participation={participation} />
              ))}
            </div>
          )}
        </div>

        {/* BGG section */}
        <div className="rounded-lg border border-border bg-card p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
              BoardGameGeek
            </h2>
            {profile.bgg_username && (
              <a
                href={`https://boardgamegeek.com/user/${profile.bgg_username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary flex items-center gap-1 hover:underline"
              >
                {profile.bgg_username}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          {profile.bgg_username ? (
            <div className="grid grid-cols-3 gap-4 text-center">
              <BggStat label="Collection" value="—" note="Coming soon" />
              <BggStat label="Plays" value="—" note="Coming soon" />
              <BggStat label="H-index" value="—" note="Coming soon" />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {is_own_profile
                ? "Add your BGG username in settings to display your collection and play history."
                : "No BGG account linked."}
            </p>
          )}
        </div>

        {/* Game Library */}
        <div className="mb-8">
          <MyLibrarySection games={library_games} showSyncButton={is_own_profile} showTabs={true} />
        </div>
      </div>
    </AppLayout>
  );
}

function EventCard({ participation }) {
  const { relationship, event } = participation;
  const startsAt = event.starts_at ? new Date(event.starts_at) : null;

  return (
    <Link href={event.paths.show} className="block group">
      <div className="rounded-lg border border-border bg-card px-4 py-3 hover:border-primary/40 hover:shadow-sm transition-all">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span className="font-medium text-sm group-hover:text-primary transition-colors truncate">
                {event.title}
              </span>
              {event.status === "cancelled" && (
                <Badge variant="destructive" className="text-xs shrink-0">Cancelled</Badge>
              )}
            </div>

            <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
              {startsAt && (
                <span className="flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" />
                  {startsAt.toLocaleDateString(undefined, {
                    weekday: "short", month: "short", day: "numeric", year: "numeric"
                  })}
                </span>
              )}
              {event.location_name && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {event.location_name}
                </span>
              )}
            </div>
          </div>

          <EventBadge relationship={participation.relationship} rsvpStatus={participation.rsvp_status} />
        </div>
      </div>
    </Link>
  );
}


function BggStat({ label, value, note }) {
  return (
    <div>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {note && <p className="text-[10px] text-muted-foreground/60 mt-0.5">{note}</p>}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}
